import { isNil } from 'lodash-es';
import { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { ZoomToSelectionIcon } from '@dynatrace/strato-icons';

import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { KeyboardControls } from '../../../core/components/interactions-overlay/constants.js';
import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { calculateInitialSelectionAreaPositions } from '../../../core/components/interactions-overlay/utils/calculate-initial-selection-area-positions.js';
import { reduceSelectionAreaVariant } from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { TOOLBAR_MESSAGES } from '../../../core/components/toolbar/messages.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { getKeyShortcutByAgent } from '../../../core/utils/get-key-shortcut-by-agent.js';
import { getPercentageFromPixel } from '../../../core/utils/get-percentage-from-pixel.js';
import { XYCHART_MESSAGES } from '../../messages/messages.js';
import { reduceInteractionMode } from '../../store/reduce-interaction-mode.js';
import {
  reduceExploreCancelAll,
  reduceExploreSelect,
} from '../../store/reducer-explorer.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
} from '../../store/selectors.js';

export const ZoomModeButton = () => {
  const setState = useSetState();
  const interactionMode = useInteractionModeState();
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Zoom);
  const { overlayRef } = useOverlay();
  const intl = useIntl();
  const selectState = useSelectState();
  const { width } = useGridPlotSize();

  const isSelected = interactionMode === INTERACTION_MODE.Zoom;
  const { resetSelectionState } = useSynchronizedSelection();

  const handleZoomMode = () => {
    if (interactionMode !== 'zoom') {
      setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
    }

    if (selectState?.start && selectState?.end) {
      if (selectState?.isKeyboardCreated) {
        reduceSelectionAreaVariant('zoom');
      } else {
        resetSelectionState?.();
        setState(reduceExploreCancelAll);
      }
    }

    setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      if (!isNil(overlayRef.current)) {
        const bounds = overlayRef.current.getBoundingClientRect();

        const [start, end] = calculateInitialSelectionAreaPositions(bounds);

        if (interactionMode !== INTERACTION_MODE.Explore) {
          setState(reduceInteractionMode(INTERACTION_MODE.Explore));
        }

        setState(
          reduceExploreSelect({
            start,
            end,
            finished: true,
            accessible: true,
            variant: 'zoom',
            selectionPercentage: getPercentageFromPixel([start, end], width),
          }),
        );
      }
    }
  };

  const tooltipKeyShortcut = `${getKeyShortcutByAgent(
    'CONTROL',
  )} ${intl.formatMessage(TOOLBAR_MESSAGES.or)} ${
    KeyboardControls.KEYBOARD_ZOOM_MODE_SHORTCUT_ALTERNATIVE
  }`;

  return (
    <ChartToolbarRenderer.Control
      prefixIcon={<ZoomToSelectionIcon />}
      selected={isSelected}
      tooltipText={intl.formatMessage(XYCHART_MESSAGES.zoomModeZoom)}
      onClick={handleZoomMode}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      tooltipKeyShortcut={tooltipKeyShortcut}
      placement="menu"
      data-dt-name="ToolbarZoomMode"
    />
  );
};
